import { createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { handleRequests } from 'redux-saga-requests';
import { createDriver } from 'redux-saga-requests-axios';
import rootReducer from 'store/reducers/RootReducer';
import rootSaga from 'store/sagas/RootSaga';
import axiosInstance from 'utils/axiosUtil';  // ← YOUR PATH
import { createBrowserHistory } from 'history';

export const history = createBrowserHistory();  // ← YOUR PATTERN

// Export this so RootSaga can import it
export let requestsSagas;

const clientAwareTransform = createTransform(
  (inboundState) => {
    return inboundState;
  },
  (outboundState, key) => {
    if (key !== 'app') return outboundState;
    let client = getQueryParam('client');
    if (client != '' && client != null) {
      client = parseInt(client, 10);
    } else {
      client = null;
    }
    return {
      ...outboundState,
      selectedClient: client || outboundState.selectedClient,
    };
  },
);

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['user', 'app'],
  transforms: [clientAwareTransform],
};

function getQueryParam(param) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
}

export default function ConfigureStore(preloadedState) {
  const connectedHistoryReducer = rootReducer(history);
  const persistedReducer = persistReducer(persistConfig, connectedHistoryReducer);
  const sagaMiddleware = createSagaMiddleware();

  // Setup redux-saga-requests
  const handleRequestsResult = handleRequests({
    driver: createDriver(axiosInstance),
    onRequest: (request, action, store) => {
      console.log('🔵 onRequest called:', action.type);
      
      // Add auth token to headers
      const token = localStorage.getItem('auth');
      if (token) {
        if (!request.headers) {
          request.headers = {};
        }
        request.headers.authorization = `Bearer ${token}`;
      } else {
        // Required for AWS auth
        if (!request.headers) {
          request.headers = {};
        }
        request.headers.authorization = 'Bearer anonymous';
      }
      
      // Add selected_client_id to params
      if (!request.params) {
        request.params = {};
      }
      
      // Get selectedClient from store if available, otherwise from localStorage
      let selectedClient = null;
      if (store && store.getState) {
        selectedClient = store.getState().app?.selectedClient;
      } else {
        // Fallback: read from localStorage
        try {
          const persistedState = localStorage.getItem('persist:root');
          if (persistedState) {
            const parsed = JSON.parse(persistedState);
            const appState = JSON.parse(parsed.app);
            selectedClient = appState.selectedClient;
          }
        } catch (e) {
          console.error('Error reading selectedClient:', e);
        }
      }
      
      request.params.selected_client_id = selectedClient;
      
      return request;
    },
    onSuccess: (response, action) => {
      console.log('✅ Request success:', action.type);
      // Log debug info for PROVIDERS_SMART_SUGGESTIONS
      if (action.type === 'ANALYTICS_PROVIDERS_SMART_SUGGESTIONS') {
        const data = response?.data || [];
        
        if (data.length > 0) {
          console.log('=== PROVIDER SMART SUGGESTIONS DEBUG ===');
          console.log('Total providers:', data.length);
          console.log('\nTop 10 Providers:\n');
          
          data.slice(0, 10).forEach((provider, index) => {
            console.log(`${index + 1}. ${provider.first_name} ${provider.last_name} (ID: ${provider.id})`);
            console.log(`   Total Points: ${provider.points || 0}`);
            
            if (provider.pointsBreakdown) {
              console.log('   Breakdown:');
              if (provider.pointsBreakdown.sharedComments > 0) {
                console.log(`     - Shared Activity Notes: +${provider.pointsBreakdown.sharedComments}`);
              }
              if (provider.pointsBreakdown.sameLocation > 0) {
                console.log(`     - Same Location: +${provider.pointsBreakdown.sameLocation}`);
              }
              if (provider.pointsBreakdown.sameOrganization > 0) {
                console.log(`     - Same Organization: +${provider.pointsBreakdown.sameOrganization}`);
              }
            }
            console.log('');
          });
          
          console.log('Full data:', data);
          console.log('=== END DEBUG ===');
        }
      } else if (action.type === 'ANALYTICS_CONTACTS_SMART_SUGGESTIONS') {
        const data = response?.data || [];
        
        if (data.length > 0) {
          console.log('=== CONTACTS SMART SUGGESTIONS DEBUG ===');
          console.log('Total contacts:', data.length);
          console.log('\nTop 10 Contacts:\n');

          data.slice(0, 10).forEach((contact, index) => {
            console.log(`${index + 1}. ${contact.first_name} ${contact.last_name} (ID: ${contact.id})`);
            console.log(`   Total Points: ${contact.points || 0}`);
            
            if (contact.pointsBreakdown) {
              console.log('   Breakdown:');
              if (contact.pointsBreakdown.sharedComments > 0) {
                console.log(`     - Shared Activity Notes: +${contact.pointsBreakdown.sharedComments}`);
              }
              if (contact.pointsBreakdown.sameLocation > 0) {
                console.log(`     - Same Location: +${contact.pointsBreakdown.sameLocation}`);
              }
              if (contact.pointsBreakdown.sameOrganization > 0) {
                console.log(`     - Same Organization: +${contact.pointsBreakdown.sameOrganization}`);
              }
            }
            console.log('');
          });
          
          console.log('Full data:', data);
          console.log('=== END DEBUG ===');
        }
      }
      return response;
    },
    onError: (error, action) => {
      console.error('❌ Request error:', action.type, error);
      
      // Handle 403 errors
      if (error.response && error.response.status === 403 && localStorage.getItem('auth')) {
        console.log('LOGOUT SENT!');
        // Dispatch logout action if needed
        // store.dispatch({ type: 'LOGOUT' });
      }
      
      return { error };
    },
  });

  const { requestsMiddleware } = handleRequestsResult;
  requestsSagas = handleRequestsResult.requestsSagas;

  const middleware = [
    sagaMiddleware,
    ...requestsMiddleware,
  ];

  const composeEnhancers =
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    
  const store = createStore(
    persistedReducer,
    preloadedState,
    composeEnhancers(applyMiddleware(...middleware)),
  );

  sagaMiddleware.run(rootSaga);

  const persistor = persistStore(store);
  return { store, persistor, history };
}